import { Routes } from '@angular/router';
import { RoleGuard } from '../../core/guard/role.guard';
import { SingleDepartmentService } from '../../shared/services/single-department.service';
import { RouterOptionTab } from '../../shared/types/router-option-tab';
import { PACKING_BOX_SPECIFY_STATION } from '../../views/packing-box/tokens/printer-station';
import { SchedulerItemApiAbstract } from '../../common/scheduler/api/item-api';
import { SchedulerDragDropApiAbstract } from '../../common/scheduler/api/drag-drop';
import { SchedulerListDragDropService } from '../../common/scheduler/services/list-drag-drop.service';
import { SchedulerStageAuthService } from '../../views/scheduler-stage/services/auth.service';
import { SchedulerStageApiService } from '../../views/scheduler-stage/api/view.service';
import { SchedulerStageViewService } from '../../views/scheduler-stage/services/view.service';
import { SchedulerStageItemService } from '../../views/scheduler-stage/services/item.service';
import { SchedulerWorkOrderApiService } from '../../common/scheduler/api/work-order.service';
import { SchedulerSubWoOnMachineApiService } from '../../common/scheduler/api/sub-wo-on-machine.service';
import { SchedulerWorkOrderOnMachineApiService } from '../../common/scheduler/api/work-order-on-machine.service';
import { SchedulerContainerService } from '../../common/scheduler/services/container.service';
import { QualityControlViewService } from '../../views/quality-control/services/view.service';
import { QualityControlAuthService } from '../../common/quality-control/services/auth.service';
import { QualityControlViewTableApiService } from '../../views/quality-control/api/table.service';
import { QC_INSPECTION_STAGE_TOKEN } from '../../common/quality-control/tokens/token';
import { MoldAuthService } from '../../common/mold/services/auth.service';
import { MoldFormService } from '../../common/mold/components/form/form.service';
import { MoldsViewService } from '../../views/molds/services/view.service';
import { MoldContainerService } from '../../common/mold/services/container.service';
import { MoldViewApiService } from '../../views/molds/api/view.service';
import { PackingBoxViewAuthService } from '../../views/packing-box/services/auth.service';
import { PackingBoxViewService } from '../../views/packing-box/services/view.service';
import { PackingBoxViewApiService } from '../../views/packing-box/api/view.service';
import { PackingBoxViewZebraApiService } from '../../views/packing-box/api/view-zebra.service';
import { PackingBoxZebraTableService } from '../../views/packing-box/components/table/table-zebra.service';
import { WebSocketApiLabelService } from '../../api/elite/websockets/label.service';
import { TableService } from '../../core/components/table/table.service';
import { QualityControlMeasureItemService } from '../../views/quality-control-inspection-data/services/measure-item.service';
import { HosesDepartmentService } from '@dep-hoses/services/hoses.service';
import { HosesAuthPackingBoxService } from '@dep-hoses/services/auth-packing-box.service';
import { HosesQualityControlMeasureItemService } from '@dep-hoses/services/measure-item.service';
import { HosesSchedulerStageAuthService } from '@dep-hoses/view-trimming/services/auth.service';
import { HosesTrimmingItemService } from '@dep-hoses/view-trimming/services/item.service';
import { HosesSchedulerStageApiService } from '@dep-hoses/view-trimming/api/view.service';
import { HosesQCRejectedItemService } from '@dep-hoses/view-qc-rejected/services/item.service';
import { HosesMoldAuthService } from '@dep-hoses/view-molds/services/auth.service';
import { HosesMoldFormService } from '@dep-hoses/view-molds/services/form.service';
import { HosesMoldViewService } from '@dep-hoses/view-molds/services/view.service';
import { HosesQualityControlViewApiService } from '@dep-hoses/view-quality-control/api/view.service';
import { HosesQcViewTableApiService } from '@dep-hoses/view-quality-control/api/table.service';
import { HosesQualityControlViewService } from '@dep-hoses/view-quality-control/services/view.service';
import { HosesAuthQcService } from '@dep-hoses/view-quality-control/services/auth.service';
import { EnvironmentProviders, Provider } from '@angular/core';
import { HOSES_ROLES_VIEWS_PERMISSIONS } from '@dep-hoses/utils/roles-actions';

const schedulerOptionsTab: RouterOptionTab[] = [
  {
    name: 'Operadores de mangueras',
    route: './',
    pathDestination: 'scheduling',
  },
  {
    name: 'Inspección',
    route: './hoses-inspection',
    pathDestination: 'hoses-inspection',
    stageName: 'Hoses Inspection',
  },
  {
    name: 'Cortes',
    route: './trimming',
    pathDestination: 'trimming',
    stageName: 'Trimming',
  },
  {
    name: 'QC',
    route: './qc',
    pathDestination: 'qc',
    stageName: 'Quality Control',
  },
];
const generateStageView = (data: {
  path: string;
  pageTitle: string;
  expectedRoles: string[];
  stageName: string;
  providers: (Provider | EnvironmentProviders)[];
}): Routes[0] => {
  return {
    path: data.path,
    loadComponent: () =>
      import('../../views/scheduler-stage/scheduler-stage.component').then(
        (m) => m.SchedulerStageComponent
      ),
    canActivate: [RoleGuard],
    data: {
      pageTitle: data.pageTitle,
      optionsTab: schedulerOptionsTab,
      expectedRole: data.expectedRoles,
      stageName: data.stageName,
    },
    providers: [
      {
        provide: SchedulerItemApiAbstract,
        useClass: SchedulerWorkOrderApiService,
      },
      {
        provide: SchedulerDragDropApiAbstract,
        useClass: SchedulerSubWoOnMachineApiService,
      },
      {
        provide: SchedulerStageAuthService,
        useClass: HosesSchedulerStageAuthService,
      },
      SchedulerStageViewService,
      SchedulerListDragDropService,
      HosesSchedulerStageApiService,
      {
        provide: SchedulerStageApiService,
        useExisting: HosesSchedulerStageApiService,
      },
      ...data.providers,
    ],
  };
};
const qualityControlView = (path: string, roles: string[]): Routes[0] => ({
  path,
  loadComponent: () =>
    import('../../views/quality-control/quality-control.component').then(
      (m) => m.QualityControlComponent
    ),
  canActivate: [RoleGuard],
  data: {
    breadcrumb: 'Quality Control',
    department: 'Hoses',
    optionsTab: schedulerOptionsTab,
    expectedRole: roles,
    stage: 'Quality Control',
  },
  providers: [
    {
      provide: QualityControlViewService,
      useClass: HosesQualityControlViewService,
    },
    { provide: QualityControlAuthService, useClass: HosesAuthQcService },
    {
      provide: QualityControlViewTableApiService,
      useClass: HosesQcViewTableApiService,
    },
    { provide: QC_INSPECTION_STAGE_TOKEN, useFactory: () => 'Quality Control' },
    {
      provide: SchedulerItemApiAbstract,
      useClass: SchedulerWorkOrderApiService,
    },
    {
      provide: SchedulerDragDropApiAbstract,
      useClass: SchedulerWorkOrderOnMachineApiService,
    },
    HosesQualityControlViewApiService,
    SchedulerListDragDropService,
    SchedulerContainerService,
  ],
});

const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'scheduling' },
  { path: 'operator-m', pathMatch: 'full', redirectTo: 'operator-machines' },

  {
    path: 'scheduling',
    loadComponent: () =>
      import(
        '../../shared/components/routing-tabs/routing-tabs.component'
      ).then((m) => m.RoutingTabsComponent),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Operadores de mangueras',
      breadcrumb: 'Form',
      expectedRole: HOSES_ROLES_VIEWS_PERMISSIONS.routerActions.scheduling,
      optionsTab: schedulerOptionsTab,
    },
    children: [
      generateStageView({
        path: 'hoses-inspection',
        expectedRoles: HOSES_ROLES_VIEWS_PERMISSIONS.routerActions.scheduling,
        stageName: 'Hoses Inspection',
        pageTitle: 'Corte de mangueras',
        providers: [
          {
            provide: SchedulerStageItemService,
            useClass: HosesTrimmingItemService,
          },
        ],
      }),
      generateStageView({
        path: 'trimming',
        expectedRoles: HOSES_ROLES_VIEWS_PERMISSIONS.routerActions.scheduling,
        stageName: 'Trimming',
        pageTitle: 'Corte de mangueras',
        providers: [
          {
            provide: SchedulerStageItemService,
            useClass: HosesTrimmingItemService,
          },
        ],
      }),
      qualityControlView(
        'qc',
        HOSES_ROLES_VIEWS_PERMISSIONS.routerActions.scheduling
      ),
      {
        path: '',
        loadComponent: () =>
          import(
            './view-scheduler-machines/view-scheduler-machines.component'
          ).then((m) => m.HosesSchedulerMachinesViewComponent),
        data: {
          pageTitle: 'Operadores de mangueras',
          breadcrumb: 'Form',
          optionsTab: schedulerOptionsTab,
        },
      },
    ],
  },

  {
    path: 'scheduling/machines-operator',
    loadComponent: () =>
      import(
        './view-scheduler-operator/view-scheduler-operator.component'
      ).then((m) => m.HosesSchedulerOperatorViewComponent),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Operadores de mangueras',
      breadcrumb: 'Form',
      expectedRole:
        HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.schedulingMachinesOperator,
    },
  },
  {
    path: 'scheduler-v2',
    loadComponent: () =>
      import('./view-machine-dashboard/view-machine-dashboard.component').then(
        (m) => m.HosesMachineDashboardSelectComponent
      ),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Tablero de máquina',
      breadcrumb: 'Form',
      expectedRole:
        HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.schedulingMachinesOperator,
    },
    children: [
      {
        path: 'machine/:machineId',
        loadComponent: () =>
          import(
            './view-machine-dashboard/machine/machine-dashboard.component'
          ).then((m) => m.HosesMachineDashboardComponent),
        canActivate: [RoleGuard],
        data: {
          pageTitle: 'Tablero de máquina',
          breadcrumb: 'Form',
          // Hide the toolbar search + page-title bars for this glance screen
          // (the shared toolbar reads this flag; other routes are unaffected).
          fullscreen: true,
          expectedRole:
            HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions
              .schedulingMachinesOperator,
        },
      },
    ],
  },
  generateStageView({
    path: 'only-trimming',
    expectedRoles:
      HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.schedulingOnlyTrimming,
    stageName: 'Trimming',
    pageTitle: 'Corte de mangueras',
    providers: [
      {
        provide: SchedulerStageItemService,
        useClass: HosesTrimmingItemService,
      },
    ],
  }),
  qualityControlView(
    'quality-control',
    HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.schedulingQualityControl
  ),
  generateStageView({
    path: 'qc-rejected',
    expectedRoles: HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.qcRejected,
    stageName: 'Rejected from QC',
    pageTitle: 'Rechazado de QC',
    providers: [
      {
        provide: SchedulerStageItemService,
        useClass: HosesQCRejectedItemService,
      },
    ],
  }),
  {
    path: 'oven',
    loadComponent: () =>
      import('./view-oven/view-oven.component').then(
        (m) => m.HosesViewOvenV2Component
      ),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Horno de mangueras',
      breadcrumb: 'Form',
      expectedRole: HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.oven,
    },
  },

  {
    path: 'validation',
    loadComponent: () =>
      import('./view-validation/view-validation.component').then(
        (m) => m.HosesViewValidationComponent
      ),
    canActivate: [RoleGuard],
    data: {
      breadcrumb: 'Validation',
      expectedRole: HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.validation,
    },
  },

  {
    path: 'molds',
    loadComponent: () =>
      import('../../views/molds/view-mold.component').then(
        (m) => m.MoldsViewComponent
      ),
    canActivate: [RoleGuard],
    data: {
      breadcrumb: 'Mold',
      expectedRole: HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.molds,
    },
    providers: [
      { provide: MoldAuthService, useClass: HosesMoldAuthService },
      { provide: MoldFormService, useClass: HosesMoldFormService },
      { provide: MoldsViewService, useClass: HosesMoldViewService },
      MoldContainerService,
      MoldViewApiService,
    ],
  },

  {
    path: 'add-qc-data',
    loadComponent: () =>
      import(
        '../../views/quality-control-inspection-data/quality-control-inspection-data.component'
      ).then((m) => m.QualityControlInspectionDataComponent),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Añadir datos de QC',
      breadcrumb: 'Form',
      department: 'Hoses',
      expectedRole: HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.addQcData,
    },
    providers: [
      {
        provide: QualityControlMeasureItemService,
        useClass: HosesQualityControlMeasureItemService,
      },
    ],
  },

  {
    path: 'packing-box',
    loadComponent: () =>
      import('../../views/packing-box/packing-box.component').then(
        (m) => m.PackingBoxComponent
      ),
    canActivate: [RoleGuard],
    providers: [
      {
        provide: PackingBoxViewAuthService,
        useClass: HosesAuthPackingBoxService,
      },
      { provide: TableService, useClass: PackingBoxZebraTableService },
      PackingBoxViewService,
      WebSocketApiLabelService,
      {
        provide: PackingBoxViewApiService,
        useClass: PackingBoxViewZebraApiService,
      },
    ],
    data: {
      breadcrumb: 'Packing Box',
      department: 'Hoses',
      pageTitle: 'Crear etiquetas',
      companyType: 'sb',
      expectedRole: HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.packingBox,
    },
  },

  {
    path: 'destinations',
    loadComponent: () =>
      import('./view-wo-destinations/wo-destinations.component').then(
        (m) => m.HosesWorkOrderDestinationsComponent
      ),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Destinos de Work Orders',
      breadcrumb: 'Destinations',
      expectedRole: HOSES_ROLES_VIEWS_PERMISSIONS.sharedActions.destinations,
    },
  },
];

export const HosesDepartmentRoutes = {
  path: 'hoses',
  children: routes,
  providers: [
    { provide: SingleDepartmentService, useClass: HosesDepartmentService },
    { provide: PACKING_BOX_SPECIFY_STATION, useValue: true },
  ],
};
